import { zodResolver } from '@hookform/resolvers/zod';
import Banner from '@savvy-kit/patterns/banner';
import InputField from '@savvy-kit/patterns/input-field';
import { Controller, useForm } from 'react-hook-form';
import { formatCurrencyForSubmit } from 'src/utils/currency';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import type { AccountTypeName } from '@savvy/constants/generated/gql-enums';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { AccountFormValues } from './add-account-schemas';
import {
  ACCOUNT_FORM_TITLES,
  ADD_ACCOUNT_SAVE_ERROR,
  DEPOSITORY_SUBTYPE_OPTIONS,
  INVESTMENT_SUBTYPE_OPTIONS,
} from './add-account-constants';
import { AddAccountFormShell } from './add-account-form-shell';
import { CLIENT_PORTAL_CREATE_ACCOUNT } from './add-account-graphql';
import { AddAccountNumericField } from './add-account-numeric-field';
import { accountFormSchema } from './add-account-schemas';
import { AddAccountSelectField } from './add-account-select-field';

type AddAccountAccountFormProps = {
  readonly accountType: Extract<AccountTypeName, 'depository' | 'investment'>;
  readonly manualEntryReason?: string;
  readonly onCancel: () => void;
  readonly onCreated: () => Promise<unknown>;
};

export function AddAccountAccountForm({
  accountType,
  manualEntryReason,
  onCancel,
  onCreated,
}: AddAccountAccountFormProps) {
  const [createAccount, { loading }] = useMutationWithErrorLogging(CLIENT_PORTAL_CREATE_ACCOUNT);
  const isInvestment = accountType === 'investment';
  const subtypeOptions = isInvestment ? INVESTMENT_SUBTYPE_OPTIONS : DEPOSITORY_SUBTYPE_OPTIONS;
  const { control, handleSubmit, formState } = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: { name: '', subtype: '', balance: '' },
  });

  const submit = handleSubmit(async (values) => {
    const subtype = subtypeOptions.find((option) => option.value === values.subtype);
    if (subtype === undefined) {
      return;
    }
    try {
      await createAccount({
        variables: {
          input: {
            currencyCode: 'USD',
            currentBalance: formatCurrencyForSubmit(values.balance) ?? '',
            name: values.name,
            subtype: subtype.value,
            type: accountType,
          },
        },
      });
      await onCreated();
    } catch (error) {
      handleMutationError(error, ADD_ACCOUNT_SAVE_ERROR);
    }
  });

  return (
    <AddAccountFormShell
      title={isInvestment ? ACCOUNT_FORM_TITLES.investment : ACCOUNT_FORM_TITLES.bank}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel={isInvestment ? 'Add investment account' : 'Add bank account'}
      submitting={loading}
    >
      {manualEntryReason !== undefined && <Banner variant="warning" description={manualEntryReason} />}
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <InputField
            {...field}
            id="add-account-name"
            label="Name"
            required
            autoFocus
            error={formState.errors.name !== undefined}
            errorMessage={formState.errors.name?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="subtype"
        render={({ field }) => (
          <AddAccountSelectField
            id="add-account-subtype"
            label="Type"
            value={field.value}
            options={subtypeOptions}
            onValueChange={field.onChange}
            errorMessage={formState.errors.subtype?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="balance"
        render={({ field }) => (
          <AddAccountNumericField
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            maxDecimals={2}
            id="add-account-balance"
            label={isInvestment ? 'Value' : 'Balance'}
            required
            prependInputAddon="$"
            errorMessage={formState.errors.balance?.message}
          />
        )}
      />
    </AddAccountFormShell>
  );
}
